import React, { useState } from "react";
import FunctionsIcon from "@mui/icons-material/Functions";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ComputedTable } from "@/lib/api/computedTables";
import { Button } from "@/components/common/Button";
import { TruncatedText } from "@/components/common/TruncatedText";
import { Inline } from "@/components/common/Stack";
import {
  ContextMenu,
  type ContextMenuItem,
} from "@/components/common/ContextMenu";
import { useContextMenu } from "@/components/hooks/useContextMenu";
import { ComputedTableSourcesMenu } from "@/components/main/overview/ComputedTableSourcesMenu";
import { ExportMenuAction } from "@/components/canvas/detailsSide/utils/ExportMenuAction";
import { showNotificationGlobal } from "@/stores/useNotificationStore";

const formatCreatedAt = (unixSeconds: number): string =>
  new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

interface Props {
  table: ComputedTable;
  refreshing: boolean;
  expanded: boolean;
  highlighted: boolean;
  onToggleExpanded: () => void;
  onRefresh: () => Promise<void>;
  onDownload: (format: "csv" | "parquet") => void;
  onExportSheets: () => void;
  onDelete: () => void;
  registerRef: (el: HTMLDivElement | null) => void;
}

/// One computed table on the overview. Refresh and Export keep their own inline
/// menus — both are things you come to this row to do. Delete is menu-only.
export const ComputedTableRow: React.FC<Props> = ({
  table,
  refreshing,
  expanded,
  highlighted,
  onToggleExpanded,
  onRefresh,
  onDownload,
  onExportSheets,
  onDelete,
  registerRef,
}) => {
  const menu = useContextMenu();
  // An open sources card or export menu is portaled outside the row, taking the
  // pointer out of its `:hover`.
  const [overlayOpen, setOverlayOpen] = useState(false);

  // "Source versions" stays out: it's a hover card, so a menu item would open
  // it only for as long as the pointer stayed still. Refresh lives inside that
  // card and only when stale, which is the one action worth lifting out.
  const menuItems: ContextMenuItem[] = [
    {
      label: refreshing ? "Refreshing…" : "Refresh",
      icon: <RefreshIcon className="icon-md" />,
      onClick: () => void onRefresh(),
      disabled: !table.stale || refreshing,
      hint: table.stale ? undefined : "already up to date",
    },
    {
      label: "Export",
      icon: <DownloadOutlinedIcon className="icon-md" />,
      onClick: () => {},
      submenu: [
        { label: "Export as CSV", onClick: () => onDownload("csv") },
        { label: "Export as Parquet", onClick: () => onDownload("parquet") },
        { label: "Export to Google Sheets", onClick: onExportSheets },
      ],
    },
    { label: "", onClick: () => {}, divider: true },
    {
      label: "Copy table ID",
      icon: <ContentCopyIcon className="icon-md" />,
      onClick: () => {
        navigator.clipboard
          .writeText(table.computed_table_id)
          .then(() => showNotificationGlobal("Table ID copied", "success"))
          .catch(() =>
            showNotificationGlobal("Couldn't copy the table ID", "error"),
          );
      },
    },
    { label: "", onClick: () => {}, divider: true },
    {
      label: "Delete",
      icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
      onClick: onDelete,
      danger: true,
    },
  ];

  const pinned = overlayOpen || menu.isOpen;

  return (
    <div
      ref={registerRef}
      className={`rounded-app transition-colors ${
        pinned ? "bg-bg-hover" : "hover:bg-bg-hover"
      }`}
      style={
        highlighted
          ? { outline: "2px solid var(--color-accent)", outlineOffset: "-2px" }
          : undefined
      }
    >
      <div
        className="group flex items-center gap-2 px-1 py-1.5"
        tabIndex={0}
        {...menu.triggerProps}
      >
        {table.description ? (
          <Button
            variant="default"
            size="xs"
            aria-label={
              expanded
                ? `Collapse ${table.name} description`
                : `Expand ${table.name} description`
            }
            onClick={onToggleExpanded}
          >
            {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
          </Button>
        ) : (
          <span className="w-6 shrink-0" />
        )}
        <span className="flex items-center justify-center size-6 rounded-app bg-accent-15 text-accent shrink-0">
          <FunctionsIcon className="icon-sm" />
        </span>
        <div className="min-w-0 flex-1">
          <TruncatedText className="text-fg-semibold cursor-default">
            {table.name}
          </TruncatedText>
        </div>
        <span
          className="font-mono text-fg-muted text-right shrink-0 whitespace-nowrap select-none cursor-default"
          style={{
            fontSize: "var(--font-size-x-small)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {`${table.row_count.toLocaleString()} rows · ${formatCreatedAt(
            table.created_at,
          )}`}
        </span>
        <Inline gap="xs" className="shrink-0">
          <ComputedTableSourcesMenu
            computedTableId={table.computed_table_id}
            tableName={table.name}
            stale={table.stale}
            onRefresh={onRefresh}
            refreshing={refreshing}
            onOpenChange={setOverlayOpen}
          />
          <ExportMenuAction
            id={`export-menu-${table.computed_table_id}`}
            variant="contextual"
            onExportCsv={() => onDownload("csv")}
            onExportParquet={() => onDownload("parquet")}
            onExportSheets={onExportSheets}
            onOpenChange={setOverlayOpen}
          />
        </Inline>
      </div>

      {table.description && (
        <div
          className="grid"
          style={{
            gridTemplateRows: expanded ? "1fr" : "0fr",
            transition:
              "grid-template-rows var(--duration-short) var(--ease-move)",
          }}
        >
          <div className="overflow-hidden" style={{ minHeight: 0 }}>
            <div className="pl-16 pr-2 pb-2 text-small text-fg-muted whitespace-pre-wrap break-words select-none cursor-default">
              {table.description}
            </div>
          </div>
        </div>
      )}

      <ContextMenu
        items={menuItems}
        position={menu.position}
        onClose={menu.close}
        ariaLabel={`Actions for ${table.name}`}
      />
    </div>
  );
};
